import { ConfirmActionModal } from "../../components/ConfirmActionModal";
import { FormProvider, useForm } from "react-hook-form";
import {
  PriceInput,
  transformPriceInput,
} from "../../components/forms/inputs/PriceInput";
import { useDepositBalance, useGetCountries } from "../../service/api";
import { SelectInput } from "../../components/forms/inputs/SelectInput";
import { ButtonAction } from "../../components/NewButtons";
import { getCountrySpecificValues } from "../../util";
import { useParams } from "react-router-dom";
import { ActionSpinner } from "../../components/ActionSpinner";
import { useSelector } from "react-redux";
import * as authSelectors from "../auth/authSelectors";
import { toastr } from "react-redux-toastr";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { getErrorMessage, useHandleFetchError } from "../../error-handler";

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

type DepositFormValues = { countryCode: string; amount: string };

const validationSchema = yup.object({
  countryCode: yup.string().required("Choose country to continue"),
  amount: yup.string().required("Enter amount to continue"),
});

export const DepositModal = ({ isOpen, onClose }: Props) => {
  const { id } = useParams<{
    id: string;
  }>();
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const { countryCode } = getCountrySpecificValues();

  const methods = useForm<DepositFormValues>({
    defaultValues: {
      countryCode,
      amount: "0",
    },
    resolver: yupResolver(validationSchema),
  });
  const { data: countries, error: getCountriesError } = useGetCountries();
  const {
    mutateAsync: depositBalance,
    isLoading: isDepositing,
  } = useDepositBalance(id);
  const { watch } = methods;

  const countryOptions = (countries || [])
    .filter((c) => c.enabled)
    .map((c) => ({
      label: c.englishName,
      value: c.countryCode,
    }));

  const onSubmit = async (values: DepositFormValues) => {
    const { countryCode, amount } = values;
    const currency = countries?.find((c) => c.countryCode === countryCode)
      ?.currency;
    if (!currency) {
      return;
    }
    const a = transformPriceInput(amount, currency);

    try {
      await depositBalance({
        amount: a,
        countryCode,
        organizationKeyName,
        userId: id,
      });
      toastr.success("Success", "Balance deposited successfully");
      onClose();
    } catch (error: any) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  const currency = countries?.find(
    (c) => c.countryCode === watch("countryCode")
  )?.currency;

  useHandleFetchError(getCountriesError);

  return (
    <FormProvider {...methods}>
      <ConfirmActionModal
        title="Deposit"
        subtitle={`How much would you like to deposit in ${currency}?`}
        isOpen={isOpen}
        onClose={onClose}
        confirmButton={
          <ButtonAction
            onClick={methods.handleSubmit(onSubmit)}
            disabled={isDepositing}
          >
            {isDepositing ? <ActionSpinner /> : "Deposit"}
          </ButtonAction>
        }
      >
        <SelectInput
          name="countryCode"
          label="User country"
          options={countryOptions}
        />
        <PriceInput
          name="amount"
          formMethods={methods}
          currencyString={currency}
          label="Amount to deposit"
        />
      </ConfirmActionModal>
    </FormProvider>
  );
};
